import { Box, Grid, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import useAPI from "../../../../api/hooks/useAPI";
import useIsIntegrated from "../../../../api/hooks/useIsIntegrated";
import { integrationKeys } from "../../../../api/queryKeys";
import LoadingIcon from "../../../../components/LoadingIcon";
import { ScreenshotAccordion } from "../../../../components/ScreenshotAccordion";
import { Toast } from "../../../../components/Toast";
import SecondaryButton from "../../../../components/buttons/SecondaryButton";
import useExpandedList from "../../../../components/hooks/ExpandedList";
import { logos } from "../../environment/icons/iconExport";
import Guideline from "../components/Guideline";
import GuidelineStep from "../components/GuidelineStep";
import GuidelineStepByStep from "../components/GuidelineStepByStep";
import IntegrationGuideAlert from "../components/IntegrationGuideAlert";
import OxConnectForm from "./OxConnectForm";
import ox_step_1 from "./images/ox_step_1.png";
import ox_step_2 from "./images/ox_step_2.png";
import ox_step_3 from "./images/ox_step_3.png";
import ox_step_4 from "./images/ox_step_4.png";

const OxIntegration = () => {
  const [searchParams] = useSearchParams();
  const [guideMode, setGuideMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    if (searchParams.has("viewonly")) {
      setGuideMode(true);
    }
  }, [searchParams]);
  const { isIntegrated, isIntegratedLoading } = useIsIntegrated("ox");
  const [loading, setLoading] = useState(false);

  const { addToExpandedList, handleExpandAll, handleCollapseAll } =
    useExpandedList();
  const { registerOxIntegration } = useAPI();

  const onSubmit = (values) => {
    setLoading(true);
    registerOxIntegration(values.api_token)
      .then((response) => {
        queryClient.invalidateQueries({
          queryKey: integrationKeys.all,
        });
        Toast.success("Ox Security integration successful");
      })
      .catch((error) => {
        if (error.response) {
          Toast.error(`Something went wrong: ${error.response.data.detail}`);
        } else {
          Toast.error("An error occurred while processing the request.");
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Box>
      <Guideline
        title={
          !isIntegrated && !loading
            ? "Once integrated, the Breeze platform will retrieve information about sensitive data, vulnerabilities, and security issues from Ox Security."
            : ""
        }
        image={logos.OxLogo}
        name={"Ox Security"}
        content={
          loading || isIntegratedLoading ? (
            <LoadingIcon size={25} />
          ) : isIntegrated && !guideMode ? (
            <Box>
              <Typography variant={"h4"}>Ox Security is integrated!</Typography>
            </Box>
          ) : (
            <div>
              {/* BUTTONS TO CONTROL EXPANSION */}
              <Box mb="20px">
                <Grid container spacing={2} direction={"row"}>
                  {guideMode && <IntegrationGuideAlert />}

                  <Grid item>
                    <SecondaryButton onClick={handleExpandAll}>
                      Expand All Screenshots
                    </SecondaryButton>
                  </Grid>
                  <Grid item>
                    <SecondaryButton onClick={handleCollapseAll}>
                      Collapse All Screenshots
                    </SecondaryButton>
                  </Grid>
                </Grid>
              </Box>

              {/* SETUP INSTRUCTIONS */}
              <GuidelineStepByStep>
                <GuidelineStep title="Login to Ox Security">
                  <Typography variant={"body2"}>
                    Login to your Ox Security account
                  </Typography>
                </GuidelineStep>
                <GuidelineStep title="Create a new API key">
                  <Typography variant={"body2"}>
                    From the left menu go to{" "}
                    <b>Settings {">"} API Key Settings </b>
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: ox_step_1,
                        altext: "Ox Security go to API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Click on the <b>CREATE API KEY</b> button.{" "}
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: ox_step_2,
                        altext: "Ox Security API Tokens Page",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                  <Typography variant={"body2"}>
                    Enter a meaningful name for the API, e.g.{" "}
                    <b>Breeze Platform</b>.
                  </Typography>
                  <Typography variant={"body2"}>
                    Under <b>API Key Type</b>, select the{" "}
                    <b>API Integration </b>
                    option.
                  </Typography>
                  <Typography variant={"body2"}>
                    Change the expiration date of the API key to the maximum
                    available.
                  </Typography>
                  <Typography variant={"body2"}>
                    Click on the <b>CREATE </b>button. To crete the API key.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: ox_step_3,
                        altext: "Ox Security crete API Tokens",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />

                  <Typography variant={"body2"}>
                    The API key will be displayed on the screen.
                    <b> Copy the key </b>and click <b>CLOSE</b>.
                  </Typography>
                  <ScreenshotAccordion
                    images={[
                      {
                        file: ox_step_4,
                        altext: "Ox Security copy API key",
                      },
                    ]}
                    onExpanded={addToExpandedList}
                  />
                </GuidelineStep>
                <GuidelineStep title="Paste the API key here">
                  <Box width={700}>
                    <OxConnectForm onSubmit={onSubmit} guideMode={guideMode} />
                  </Box>
                </GuidelineStep>
              </GuidelineStepByStep>
            </div>
          )
        }
      />
    </Box>
  );
};

export default OxIntegration;
